/**
 * Tiny in-browser reverse-geocoder.
 *
 * iter-108: backend now stamps each clock punch with an address label,
 * but entries created BEFORE that change only have raw lat/lng. This
 * helper fills the gap on the client — fetches Nominatim once per
 * unique coord (rounded to 4dp) and caches the result in localStorage
 * so we don't re-hit the network on every render.
 */

const CACHE_KEY = "planflow.revgeo.cache.v1";
const MAX = 500;

function loadCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}

function saveCache(obj) {
    try {
        // Trim if oversized to keep localStorage small.
        const keys = Object.keys(obj);
        if (keys.length > MAX) {
            const trimmed = {};
            for (const k of keys.slice(-MAX)) trimmed[k] = obj[k];
            obj = trimmed;
        }
        localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
    } catch { /* ignore */ }
}

const inFlight = new Map(); // key → Promise<string|null>

export async function reverseGeocode(lat, lng) {
    if (lat == null || lng == null) return null;
    const key = `${(+lat).toFixed(4)},${(+lng).toFixed(4)}`;
    const cache = loadCache();
    if (key in cache) return cache[key];
    if (inFlight.has(key)) return inFlight.get(key);
    const p = (async () => {
        try {
            const url = `https://nominatim.openstreetmap.org/reverse?format=json&zoom=16&lat=${lat}&lon=${lng}`;
            const r = await fetch(url, {
                headers: { "Accept-Language": navigator.language || "en" },
            });
            if (!r.ok) return null;
            const data = await r.json();
            const a = data?.address || {};
            const parts = [
                a.road || a.pedestrian || a.footway || "",
                a.house_number || "",
                a.city || a.town || a.village || a.hamlet || "",
                a.state || a.region || "",
            ].filter(Boolean);
            const label = (parts.join(", ") || data?.display_name || "").trim();
            const final = label || null;
            const c = loadCache();
            c[key] = final;
            saveCache(c);
            return final;
        } catch {
            return null;
        } finally {
            inFlight.delete(key);
        }
    })();
    inFlight.set(key, p);
    return p;
}
